import React, {
  useRef,
  useEffect,
  useMemo,
  useState,
  Fragment,
  memo,
} from 'react';
import { Bar, Line, Chart as Charts } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import 'chartjs-plugin-annotation';
import 'chartjs-plugin-style';
import cn from 'classnames';
import get from 'lodash/get';
import moment from 'moment';

import {
  loggedUserIdSelector,
  targetUserIdSelector,
  targetUserSelector,
} from '../../../redux/context/selectors';
import Loader from '../../common/Loader';
import Icon from '../../Icon';
import { getSafeDescriptionDataHistoricList } from '../utils';
import {
  CHART_FILTERS_TYPE,
  DATA_SOURCE_TYPE,
  VITALS_ACTIVITY_TYPE,
} from '../constants';
import { useCCDSourceWeightBMI } from './useCCDSourceWeightBMI';
import { useSaturationDataGraf } from './useSaturationDataGraf';
import {
  combinedPendingDataModalRequest,
  combinedPendingDataRequest,
} from '../../../redux/profiles/patientHealthboard/selectors';
import { useActionFilters } from './Filters/useActionFilters';
import { customDrawRectangle } from './customDrawRectangle';
import { usePluginForChart } from './usePluginForChart';
import { useAverageData } from './useAverageData';
import { useDataGraf } from './useDataGraf';
import { Filters } from './Filters';
import { Legend } from './Legend';
import styles from './index.styl';
import { useSafeDataSourceTitle } from '../../common/Charts/hooks';

const {
  SATURATION,
  HEIGHT,
  WEIGHT,
  BMI,
  STEPS,
  DISTANCE,
  HEART_RATE,
  BLOOD_GLUCOSE,
} = VITALS_ACTIVITY_TYPE;
const { MONTH, DAY } = CHART_FILTERS_TYPE;
const { ALL_SOURCES } = DATA_SOURCE_TYPE;

function Chart({
  formatMessage,
  type,
  setActiveFilter,
  activeFilter,
  baseDate,
  singleDayMode,
  isWeightInKg,
  isHeightInMeters,
  setHistoricSourceType,
  isMilligramsPerDeciliter,
  mainUser,
}) {
  useEffect(() => {
    if (Charts) {
      Charts.elements.Rectangle.prototype.draw = customDrawRectangle;
    }
  }, []);

  const stepsOrDistance = [STEPS, DISTANCE].includes(type);
  const isCandleType = [SATURATION, HEART_RATE, BLOOD_GLUCOSE].includes(type);

  const [sourceType, setSourceType] = useState(ALL_SOURCES);

  const targetUserId = useSelector(targetUserIdSelector);
  const loggedUserId = useSelector(loggedUserIdSelector);

  const targetUserFirstName = get(
    useSelector(targetUserSelector),
    'firstName',
    '',
  );

  const safeUserId = targetUserId || loggedUserId;
  const dispatch = useDispatch();

  const pendingChart = useSelector(combinedPendingDataRequest);
  const pendingChartModal = useSelector(combinedPendingDataModalRequest);
  const getDataAction = useActionFilters(type, singleDayMode);

  const safePending = singleDayMode ? pendingChartModal : pendingChart;

  const { getSafeDataSourceTitle } = useSafeDataSourceTitle();

  useEffect(() => {
    return () => {
      if (setActiveFilter) {
        setActiveFilter(MONTH.type);
      }
    };
  }, []);

  useEffect(() => {
    if (getDataAction && type && type !== HEIGHT)
      dispatch(
        getDataAction.action({
          userId: safeUserId,
          period: singleDayMode ? DAY.type : MONTH.type,
          ...(baseDate && { baseDate: moment(baseDate).format('YYYY-MM-DD') }),
        }),
      );
  }, [type, baseDate, safeUserId]);

  useEffect(() => {
    setSourceType(ALL_SOURCES);
  }, [activeFilter]);

  const handleClick = filterType => {
    dispatch(getDataAction.clear());
    setActiveFilter(filterType);
    dispatch(getDataAction.action({ userId: safeUserId, period: filterType }));
  };

  const currentChartInstance = useRef();

  const { data, options, isGrafTypeLine } = useDataGraf({
    formatMessage,
    dataType: type,
    filterType: activeFilter,
    chartInstanceRef: currentChartInstance,
    singleDayMode,
    isWeightInKg,
    mainUser,
    targetUserFirstName,
    isMilligramsPerDeciliter,
  });

  const { bmiWeightData, bmiWeightOptions } = useCCDSourceWeightBMI({
    rowData: data,
    activeFilter,
    singleDayMode,
    options,
    dataType: type,
    sourceType,
    currentChartInstance,
    formatMessage,
    mainUser,
    targetUserFirstName,
    isWeightInKg,
  });

  const { saturationData, saturationOptions } = useSaturationDataGraf({
    rowData: data,
    activeFilter,
    singleDayMode,
    options,
    isCandleType,
    sourceType,
    currentChartInstance: currentChartInstance.current,
    formatMessage,
    mainUser,
    targetUserFirstName,
    type,
    isMilligramsPerDeciliter,
  });

  const { safeData, safeOptions } = useMemo(() => {
    switch (type) {
      case DISTANCE:
      case STEPS:
      case HEIGHT:
        return { safeData: data, safeOptions: options };
      case BMI:
      case WEIGHT:
        return { safeData: bmiWeightData, safeOptions: bmiWeightOptions };
      case HEART_RATE:
      case SATURATION:
      case BLOOD_GLUCOSE:
        return { safeData: saturationData, safeOptions: saturationOptions };
    }
  }, [type, data, options, saturationOptions, saturationData]);

  const {
    formatValue,
    title,
    metric,
    date: rangeDate,
    averageLine,
  } = useAverageData(
    formatMessage,
    safeData,
    type,
    activeFilter,
    singleDayMode,
    isWeightInKg,
    isHeightInMeters,
    isMilligramsPerDeciliter,
  );

  const plugins = usePluginForChart(isGrafTypeLine, isCandleType);

  const isEmptyState = !safeData.averageOrTotal.value;
  const safeRangeTitle = isCandleType ? rangeDate : `${title} ${rangeDate}`;
  const safeRangeTitleForOneDay = `${getSafeDescriptionDataHistoricList(
    type,
    formatMessage,
    true,
  )} ${rangeDate}`;

  const sourceTypeTitle = getSafeDataSourceTitle(
    sourceType,
    formatMessage,
    mainUser,
    targetUserFirstName,
    stepsOrDistance,
  );

  const deviceTypeTitle = getSafeDataSourceTitle(
    DATA_SOURCE_TYPE.DEVICES,
    formatMessage,
    mainUser,
    targetUserFirstName,
    stepsOrDistance,
  );

  const handleChooseDataSource = currentType => {
    if (currentType === sourceType) {
      if (setHistoricSourceType) {
        setHistoricSourceType(ALL_SOURCES);
      }
      setSourceType(ALL_SOURCES);
    } else {
      if (setHistoricSourceType) {
        setHistoricSourceType(currentType);
      }
      setSourceType(currentType);
    }
  };

  return type !== HEIGHT ? (
    <div
      className={cn(styles.root, { [styles.singleDayPadding]: singleDayMode })}
    >
      {singleDayMode ? (
        <div className={styles.singleDayRoot}>
          <div className={styles.valueWrapper}>
            <div className={styles.value}>{formatValue || ''}</div>
            <div className={styles.metric}>{metric}</div>
          </div>
          <div className={styles.rightSideWrapper}>
            <div className={styles.date}>{safeRangeTitleForOneDay}</div>
            {stepsOrDistance && (
              <div className={styles.dataSourceTitle}>{deviceTypeTitle}</div>
            )}
            {!stepsOrDistance && (
              <div className={styles.dataSourceTitle}>{sourceTypeTitle}</div>
            )}
          </div>
        </div>
      ) : (
        <Fragment>
          <div className={styles.header}>
            <div className={styles.averageValueWrapper}>
              <div className={styles.averageValue}>
                {safePending || isEmptyState ? '-' : formatValue}
              </div>
              <div className={styles.averageMetric}>{metric}</div>
            </div>
            <Filters activeFilter={activeFilter} handleClick={handleClick} />
          </div>
          <div className={styles.averageDate}>{safeRangeTitle}</div>
          {stepsOrDistance && (
            <div className={styles.dataSourceTitle}>{deviceTypeTitle}</div>
          )}
          {!stepsOrDistance && (
            <div className={styles.dataSourceTitle}>{sourceTypeTitle}</div>
          )}
        </Fragment>
      )}

      {safePending && <Loader />}
      {isEmptyState && !safePending && (
        <div className={styles.emptyStateWrapper}>
          <Icon name="ico-1000-graph" size={60} />
          <div className={styles.emptyStateDescription}>
            {formatMessage({ id: 'patient.healthboard.graf.emptyState' })}
          </div>
        </div>
      )}
      {!isEmptyState && !safePending && type !== HEIGHT && (
        <div
          className={cn(styles.grafWrapper, {
            [styles.grafWrapperPadding]: singleDayMode,
          })}
        >
          {isGrafTypeLine ? (
            <Line
              data={safeData}
              height={80}
              plugins={plugins}
              options={{ ...safeOptions, ...averageLine }}
              ref={currentChartInstance}
            />
          ) : (
            <Bar
              data={safeData}
              height={80}
              options={{ ...safeOptions, ...averageLine }}
              ref={currentChartInstance}
              plugins={plugins}
              redraw
            />
          )}
          {!stepsOrDistance && (
            <Legend
              formatMessage={formatMessage}
              sourceType={sourceType}
              handleClick={dataSourceType =>
                handleChooseDataSource(dataSourceType)
              }
              availableDataSource={safeData.availableDataSource}
              targetUserFirstName={targetUserFirstName}
              dataType={type}
              mainUser={mainUser}
              activeFilter={activeFilter}
              setSourceType={setSourceType}
            />
          )}
        </div>
      )}
    </div>
  ) : null;
}

Chart.propTypes = {
  formatMessage: PropTypes.func,
  type: PropTypes.string,
  setActiveFilter: PropTypes.func,
  activeFilter: PropTypes.string,
  baseDate: PropTypes.string,
  singleDayMode: PropTypes.bool,
  isWeightInKg: PropTypes.bool,
  isHeightInMeters: PropTypes.bool,
  isMilligramsPerDeciliter: PropTypes.bool,
  setHistoricSourceType: PropTypes.func,
  mainUser: PropTypes.bool,
};

export default memo(Chart);



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/Chart/index.jsx